import React, { useEffect, useRef, useState } from 'react';
import { useManagedUsers } from '../UsersContext';
import { apiJson } from '../apiClient';
import { normalizeRole } from '../utils/userLocale';
import {
  SUCCESS_THEN_REDIRECT_MS,
  waitUntilMinElapsed,
} from '../utils/submitFeedback';
import './UsersPage.css';

const UsersAddPage = ({ onNavigate, user }) => {
  const { addUser } = useManagedUsers();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [role, setRole] = useState('employee');
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [phase, setPhase] = useState('idle');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const redirectRef = useRef(null);

  useEffect(() => {
    return () => {
      if (redirectRef.current) clearTimeout(redirectRef.current);
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await apiJson('/owner/branches');
        if (!mounted) return;
        const list = data.branches || [];
        setBranches(list);
        const preset = sessionStorage.getItem('stocktrack_add_user_branch');
        if (preset && list.some((b) => String(b.id) === preset)) {
          setBranchId(preset);
          sessionStorage.removeItem('stocktrack_add_user_branch');
        } else if (list.length) {
          setBranchId(String(list[0].id));
        }
      } catch {
        if (!mounted) return;
        setBranches([]);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  if (normalizeRole(user?.role) !== 'owner') {
    return (
      <div className="users-shell page-bleed">
        <div className="users-card users-card--narrow">
          <h1 className="users-title">Add user</h1>
          <p className="users-lede">Only an owner account can add users.</p>
          <button type="button" className="users-btn-link" onClick={() => onNavigate('overview')}>
            ← Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  const busy = phase === 'loading' || phase === 'success';
  const pwdLen = password.length;
  const pwdHint =
    pwdLen === 0 ? 'At least 8 characters.' : pwdLen < 8 ? `${8 - pwdLen} more character(s) needed.` : 'Length OK.';

  const isStaffRole = role === 'employee' || role === 'manager';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (phase !== 'idle') return;
    if (redirectRef.current) {
      clearTimeout(redirectRef.current);
      redirectRef.current = null;
    }
    setMessage('');
    setError('');
    setPhase('loading');
    const started = Date.now();
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      setPhase('idle');
      return;
    }
    if (password !== passwordConfirm) {
      setError('Passwords do not match.');
      setPhase('idle');
      return;
    }
    if (!branchId) {
      setError('Select a branch for this worker.');
      setPhase('idle');
      return;
    }
    if (isStaffRole && !phone.trim()) {
      setError('Phone number is required for staff and managers (they can sign in with it).');
      setPhase('idle');
      return;
    }
    if (!isStaffRole && !email.trim()) {
      setError('Email is required for owner accounts.');
      setPhase('idle');
      return;
    }
    const res = await addUser({
      name,
      email: email.trim() || undefined,
      phone: phone.trim() || undefined,
      role,
      password,
      branchId: Number(branchId),
    });
    await waitUntilMinElapsed(started);
    if (!res.ok) {
      setError(res.error || 'Could not add user.');
      setPhase('idle');
      return;
    }
    setMessage('User saved on the server.');
    setName('');
    setEmail('');
    setPhone('');
    setPassword('');
    setPasswordConfirm('');
    setPhase('success');
    redirectRef.current = setTimeout(() => {
      redirectRef.current = null;
      setPhase('idle');
      setMessage('');
      if (typeof onNavigate === 'function') onNavigate('users-list');
    }, SUCCESS_THEN_REDIRECT_MS);
  };

  return (
    <div className="users-shell users-shell--add page-bleed">
      <div className="users-add-header">
        <div>
          <h1 className="users-title">Add user</h1>
          <p className="users-lede users-add-intro">
            Choose a branch and role: <strong>Staff</strong> (day-to-day), <strong>Manager</strong> (branch lead +
            reports), or <strong>Owner</strong> (full business access).
          </p>
        </div>
        <button type="button" className="users-btn-ghost" onClick={() => onNavigate('users-list')} disabled={busy}>
          ← All users
        </button>
      </div>

      <div className="users-card users-form-card">
        <form className="users-form-grid" onSubmit={handleSubmit} noValidate>
          <div className="users-form-section-title">Account details</div>


          <label className="users-field users-field-span2">
            <span className="users-field-label">Branch</span>
            <select
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
              required
              disabled={busy || !branches.length}
            >
              {!branches.length ? (
                <option value="">Add a branch first (Team → Branches)</option>
              ) : (
                branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))
              )}
            </select>
          </label>

          <label className="users-field users-field-span2">
            <span className="users-field-label">Role</span>
            <select value={role} onChange={(e) => setRole(e.target.value)} required disabled={busy}>
              <option value="employee">Staff — POS, stock, deliveries at this branch</option>
              <option value="manager">Manager — branch reports, approvals, team view</option>
              <option value="owner">Owner — all branches, billing, full settings</option>
            </select>
            <span className="users-form-hint">
              Staff for counter/yard work; manager for a branch supervisor; owner for a business partner.
            </span>
          </label>

          <label className="users-field users-field-span2">
            <span className="users-field-label">Full name</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="e.g. Charles Baraka"
              disabled={busy}
              autoComplete="name"
            />
          </label>

          <label className="users-field users-field-span2">
            <span className="users-field-label">
              Mobile phone {isStaffRole ? <span className="users-required">*</span> : <span className="users-optional">(optional)</span>}
            </span>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required={isStaffRole}
              placeholder="0712 345 678"
              disabled={busy}
              autoComplete="tel"
            />
            <span className="users-form-hint">
              Staff and managers can sign in with this number and their password.
            </span>
          </label>

          <label className="users-field users-field-span2">
            <span className="users-field-label">
              Work email {isStaffRole ? <span className="users-optional">(optional)</span> : <span className="users-required">*</span>}
            </span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required={!isStaffRole}
              placeholder={isStaffRole ? 'Optional — for notifications' : 'name@company.com'}
              disabled={busy}
              autoComplete="off"
            />
          </label>

          <div className="users-form-section-title users-form-section-title--spaced">Sign-in password</div>
          <p className="users-form-hint users-field-span2">
            {isStaffRole
              ? 'They can sign in with their phone number (or email if you set one) and this password.'
              : 'Owners must sign in with email and this password.'}
          </p>

          <label className="users-field">
            <span className="users-field-label">Password</span>
            <div className="users-input-with-toggle">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                disabled={busy}
                placeholder="Minimum 8 characters"
                id="users-new-password"
              />
              <button
                type="button"
                className="users-toggle-visibility"
                tabIndex={-1}
                onClick={() => setShowPassword((v) => !v)}
                disabled={busy}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
            <span className={`users-pwd-meter ${pwdLen >= 8 ? 'users-pwd-meter--ok' : ''}`}>{pwdHint}</span>
          </label>

          <label className="users-field">
            <span className="users-field-label">Confirm password</span>
            <div className="users-input-with-toggle">
              <input
                type={showPassword2 ? 'text' : 'password'}
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                disabled={busy}
                placeholder="Re-enter password"
              />
              <button
                type="button"
                className="users-toggle-visibility"
                tabIndex={-1}
                onClick={() => setShowPassword2((v) => !v)}
                disabled={busy}
              >
                {showPassword2 ? 'Hide' : 'Show'}
              </button>
            </div>
          </label>

          {error && <div className="users-alert users-alert-error users-field-span2">{error}</div>}
          {message && <div className="users-alert users-alert-success users-field-span2">{message}</div>}

          <div className="users-form-actions users-field-span2">
            <button type="submit" className="users-btn-primary" disabled={busy}>
              {phase === 'loading' ? (
                <>
                  <span className="submit-spinner" aria-hidden />
                  Saving…
                </>
              ) : phase === 'success' ? (
                'Redirecting…'
              ) : (
                'Create user'
              )}
            </button>
            <button type="button" className="users-btn-secondary" onClick={() => onNavigate('users-list')} disabled={busy}>
              Cancel
            </button>
          </div>
        </form>
      </div>

      <p className="users-page-foot">
        <button type="button" className="users-btn-link" onClick={() => onNavigate('overview')} disabled={busy}>
          ← Back to dashboard
        </button>
      </p>
    </div>
  );
};

export default UsersAddPage;
